import { MAX_PERIOD_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import { MODE } from '../PagesQueriesChanges/scope';
import {
    MAX_NAME_LENGTH,
    MAX_PATTERN_LENGTH,
    MAX_TERM_LENGTH,
    MAX_TERMS,
    MAX_TOPICS,
    POLICIES,
    POLICY,
    topicError,
} from './topics';

/**
 * How block 02 orders the topics: the same three orders, with the same meaning, as the rows of
 * Pages, Queries & Changes. A topic that grew or declined moved in the metric — the order makes no
 * claim about why, and nothing here diagnoses the content behind it.
 */
export const MODES = [MODE.POPULAR, MODE.GAINING, MODE.DECLINING];

/** The metrics the order and the trend open in. */
export const ORDER_METRICS = ['clicks', 'impressions'];

/**
 * What block 04 lists for the active topic. The two are different aggregations — queries are
 * aggregated by property, pages by page — so switching changes what the totals mean, and the
 * block says so rather than carrying one view's numbers into the other.
 */
export const CONTRIBUTORS = { QUERIES: 'queries', PAGES: 'pages' };

export const CONTRIBUTOR_DIMENSION = {
    [CONTRIBUTORS.QUERIES]: 'query',
    [CONTRIBUTORS.PAGES]: 'page',
};

/**
 * The scope this report runs under: the shared one, plus the session's topics, the topic the
 * detail blocks are about, how the comparison is ordered and which contributors block 04 lists.
 *
 * The topics are part of the scope rather than a view over fetched rows, because they are in the
 * request — which is why they sit behind the same Apply as the dates. The window is the shared
 * headline bound; only block 04 carries a query or page dimension, and it shortens its own window
 * through the shared detail helper and says so.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        // Session-only, in the order they were added. Nothing is stored in the account or in the
        // browser; the topics travel with the applied scope in the page's address.
        topics: [],
        topic: '',
        mode: MODE.POPULAR,
        metric: 'clicks',
        contributors: CONTRIBUTORS.QUERIES,
    };
}

/**
 * The draft as a scope, with every reason it cannot be applied.
 *
 * A session with no topics is refused rather than answered: with nothing to filter by, the report
 * would quietly read every query on the property and present it under a topic heading.
 */
export function resolveReportScope(draft, t) {
    const scope = resolveScope(draft, t, { maxDays: MAX_PERIOD_DAYS });

    const topics = [];
    for (const topic of draft.topics ?? []) {
        const error = topicError(topic, t);
        if (error) scope.errors.push(`${topic.name} — ${error}`);
        else topics.push(topic);
    }

    if (!topics.length) {
        scope.errors.push(t('tools.topic_clusters.errors.no_topics'));
    } else if (topics.length > MAX_TOPICS) {
        scope.errors.push(t('tools.topic_clusters.errors.too_many_topics', { max: MAX_TOPICS }));
    }

    const active = topics.find((topic) => topic.name === draft.topic) ?? topics[0] ?? null;
    const contributors = Object.values(CONTRIBUTORS).includes(draft.contributors)
        ? draft.contributors
        : CONTRIBUTORS.QUERIES;

    return {
        ...scope,
        topics,
        // The topic blocks 03 and 04 are about. Null once the user has cleared it, which leaves
        // the comparison standing and the detail blocks waiting for a pick.
        topic: active?.name ?? null,
        mode: MODES.includes(draft.mode) ? draft.mode : MODE.POPULAR,
        metric: ORDER_METRICS.includes(draft.metric) ? draft.metric : 'clicks',
        contributors,
    };
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['topics', 'topic', 'mode', 'metric', 'contributors'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own controls too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

const isText = (value, max) => typeof value === 'string' && !!value.trim() && value.length <= max;

/**
 * One topic as the address can carry it, or null.
 *
 * A query string is typed by hand as easily as it is copied, so a topic is taken only when its
 * policy is one this report offers and its name, terms and expression are text within the bounds a
 * typed one may have. Whether an expression reads is left to `topicError`, which reports it as a
 * scope error the user can see rather than dropping the topic silently here.
 */
function readTopic(topic) {
    if (!topic || typeof topic !== 'object') return null;
    if (!POLICIES.includes(topic.policy)) return null;
    if (!isText(topic.name, MAX_NAME_LENGTH)) return null;

    if (topic.policy === POLICY.REGEX) {
        if (!isText(topic.pattern, MAX_PATTERN_LENGTH)) return null;
        return { name: topic.name.trim(), policy: topic.policy, terms: [], pattern: topic.pattern };
    }

    const terms = (Array.isArray(topic.terms) ? topic.terms : [])
        .filter((term) => isText(term, MAX_TERM_LENGTH))
        .map((term) => term.trim())
        .slice(0, MAX_TERMS);
    if (!terms.length) return null;

    return { name: topic.name.trim(), policy: topic.policy, terms, pattern: '' };
}

/** The draft this report starts from when its address already describes one. */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (params.topic) draft.topic = params.topic;
    if (MODES.includes(params.mode)) draft.mode = params.mode;
    if (ORDER_METRICS.includes(params.metric)) draft.metric = params.metric;
    if (Object.values(CONTRIBUTORS).includes(params.contributors)) {
        draft.contributors = params.contributors;
    }

    if (params.topics) {
        try {
            const parsed = JSON.parse(params.topics);
            const names = new Set();
            // A name is the topic's identity everywhere on the page, so a repeated one keeps only
            // its first definition rather than two topics answering to the same name.
            const topics = (Array.isArray(parsed) ? parsed : [])
                .map(readTopic)
                .filter((topic) => topic && !names.has(topic.name) && names.add(topic.name));
            if (topics.length) draft.topics = topics;
        } catch (err) {
            // A topics parameter that is not JSON is not a report anyone meant to open; the page
            // starts with no topics rather than refusing to open at all.
            console.log(err);
        }
    }

    return draft;
}

/** The applied scope as query parameters. */
export function reportParamsFromScope(scope) {
    return {
        ...scopeParamsFromScope(scope),
        topics: scope.topics?.length ? JSON.stringify(scope.topics) : undefined,
        topic: scope.topic ?? undefined,
        mode: scope.mode,
        metric: scope.metric,
        contributors: scope.contributors,
    };
}
